/**
 * TaxReconciliation - Basis-Aware P&L vs 1065/K-1 Cross-Check Component
 *
 * Compares bank-derived or client-provided P&L totals against tax-reported 1065 figures
 * with thresholds that adapt based on accounting basis (cash vs accrual).
 *
 * Based on Darryl's guidance:
 * - Tax returns are almost always cash basis
 * - Bank-derived P&Ls are inherently cash basis
 * - Client P&Ls from QuickBooks may be either basis - must be declared and disclosed
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Scale,
  FileText,
  Calculator,
  Settings2,
  Store,
  ArrowRight
} from 'lucide-react';
import { FinancialValue } from '../../components/shared/FinancialValue';

// Factor type emitted by the backend when merchant statements close a
// revenue variance gap.  Presence of this factor (regardless of amount
// threshold — the backend owns that decision) is the signal to swap the
// "Investigate" pill for an "Explained — Merchant Evidence" pill.
const MERCHANT_RECON_FACTOR_TYPE = 'merchant_statement_reconciliation';

const hasMerchantReconciliation = (factors?: VarianceFactor[]): boolean =>
  !!factors?.some(f => f.type === MERCHANT_RECON_FACTOR_TYPE);

interface VarianceFactor {
  text: string;
  amount: number | null;
  type: string;
  count: number | null;
}

interface BucketExplanation {
  pl_total: number;
  tax_total: number;
  variance: number;
  variance_pct: number | null;
  factors: VarianceFactor[];
  adjusted_variance?: number;
  adjusted_variance_pct?: number | null;
}

interface YearExplanations {
  revenue: BucketExplanation;
  payroll: BucketExplanation;
  cogs: BucketExplanation;
  cross_validation: { factors: VarianceFactor[] };
}

interface TaxReconciliationProps {
  plData: Record<string, any[]>;  // P&L data by year (from canonical_data)
  tax1065Files: any[];            // 1065 form data
  k1Forms: any[];                 // K-1 form data
  varianceExplanations?: Record<string, YearExplanations>;  // from canonical_data
  /**
   * Optional callback fired when the user clicks "View reconciliation" on a
   * merchant-explained variance card.  If omitted, the drill-down link is
   * hidden.  Hosts (e.g. SidebarStep3) wire this to setActiveView('revenue_sources').
   */
  onNavigateToRevenueSources?: () => void;
}

interface YearComparison {
  year: number;
  revenue: { pl: number; tax: number; diff: number; pct: number };
  payroll: { pl: number; tax: number; diff: number; pct: number };
  cogs: { pl: number; tax: number; diff: number; pct: number };
  status: 'match' | 'review' | 'investigate';
}

// Basis options with their thresholds
const basisOptions = [
  {
    id: 'bank_cash',
    label: 'Bank-Derived (Cash) vs 1065 (Cash)',
    shortLabel: 'Bank vs 1065',
    description: 'Tightest thresholds — both cash basis from same underlying transactions',
    thresholds: { review: 5, investigate: 10 },
    color: 'emerald'
  },
  {
    id: 'client_cash',
    label: 'Client P&L (Cash) vs 1065 (Cash)',
    shortLabel: 'Client Cash vs 1065',
    description: 'Tight thresholds — allow for QuickBooks categorization differences',
    thresholds: { review: 8, investigate: 15 },
    color: 'blue'
  },
  {
    id: 'client_accrual',
    label: 'Client P&L (Accrual) vs 1065 (Cash)',
    shortLabel: 'Accrual vs Cash',
    description: 'Wider thresholds — AR/AP timing differences are expected',
    thresholds: { review: 15, investigate: 25 },
    color: 'amber'
  },
];

// Basis-specific notes for each line item
const basisNotes: Record<string, Record<string, string>> = {
  revenue: {
    bank_cash: "Both cash basis — variance should be small. Large gap suggests non-revenue deposits (transfers, loans, owner contributions).",
    client_cash: "Both cash basis — check for QuickBooks categorization differences or unrecorded adjustments.",
    client_accrual: "Expected: accrual revenue includes unbilled AR. Compare year-end AR balances to explain gap."
  },
  payroll: {
    bank_cash: "Check guaranteed payments to partners, 1099 vs W-2 classification, officer compensation.",
    client_cash: "Review for contractor payments categorized as payroll, or benefits included in one but not the other.",
    client_accrual: "Accrued payroll liabilities may cause timing differences. Check year-end accruals."
  },
  cogs: {
    bank_cash: "Should align closely. Check for inventory purchases vs COGS timing on tax return.",
    client_cash: "Minor variance from categorization. Verify Section 263A if applicable.",
    client_accrual: "AP timing will cause variance. Check prepaid expenses and inventory accruals."
  }
};

const formatPercent = (pct: number): string => {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

// Token-based color roles for the three variance states. Match/done →
// status-done (green, success only). Review + investigate are variance
// signals → brand orange tint (no amber/red per design system).
const STATUS_STYLES: Record<'match' | 'review' | 'investigate', {
  color: string;
  bg: string;
  border: string;
}> = {
  match: {
    color: 'var(--color-verified)',
    bg: 'transparent',
    border: 'var(--color-verified)',
  },
  review: {
    color: 'var(--color-needs-human-text)',
    bg: 'var(--color-needs-human-tint)',
    border: 'var(--color-needs-human-tint-bd)',
  },
  investigate: {
    color: 'var(--color-needs-human-text)',
    bg: 'var(--color-needs-human-tint)',
    border: 'var(--color-needs-human-tint-bd)',
  },
};

const getVarianceStatus = (pct: number, basisId: string): {
  status: 'match' | 'review' | 'investigate';
  label: string;
} => {
  const basis = basisOptions.find(b => b.id === basisId) || basisOptions[0];
  const abs = Math.abs(pct);

  if (abs <= basis.thresholds.review) {
    return {
      status: 'match',
      label: 'Within Tolerance',
    };
  }
  if (abs <= basis.thresholds.investigate) {
    return {
      status: 'review',
      label: 'Review Needed',
    };
  }
  return {
    status: 'investigate',
    label: 'Investigate',
  };
};

const TaxReconciliation: React.FC<TaxReconciliationProps> = ({
  plData,
  tax1065Files,
  k1Forms,
  varianceExplanations,
  onNavigateToRevenueSources,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [yearBasis, setYearBasis] = useState<Record<number, string>>({});
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Calculate annual P&L totals from canonical data
  const plAnnualTotals = useMemo(() => {
    const totals: Record<number, { revenue: number; payroll: number; cogs: number }> = {};

    if (!plData) return totals;

    Object.entries(plData).forEach(([yearKey, months]) => {
      const yearMatch = yearKey.match(/(\d{4})/);
      if (!yearMatch) return;
      const year = parseInt(yearMatch[1]);

      if (!totals[year]) {
        totals[year] = { revenue: 0, payroll: 0, cogs: 0 };
      }

      (months as any[]).forEach(m => {
        totals[year].revenue += m.revenue || m.gross_revenue || 0;
        totals[year].payroll += m.payroll || m.employee_wages || 0;
        totals[year].cogs += m.cogs || 0;
      });
    });

    return totals;
  }, [plData]);

  // Extract 1065 data by year
  const tax1065ByYear = useMemo(() => {
    const byYear: Record<number, any> = {};

    tax1065Files.forEach(file => {
      const taxData = file.data || {};
      const year = taxData.tax_year || file.year;
      if (year) {
        const wages = Number(taxData.salaries_wages) || 0;
        const guaranteedPayments = Number(taxData.guaranteed_payments) || 0;
        const taxesLicenses = Number(taxData.taxes_licenses) || 0;
        const employeeBenefits = Number(taxData.employee_benefits) || 0;

        // EMPLOYEE compensation only (Line 9 + 14 + 19).  This is what maps
        // 1:1 to an accrual P&L "Payroll / Wages" line.  We intentionally
        // EXCLUDE Line 10 guaranteed payments to partners — those are
        // partner draws, not employee payroll, and typically appear on the
        // P&L as a separate "Partner Compensation" / "Owner Draw" line.
        const employeeCompensation =
          Number(taxData.total_employee_compensation) ||
          (wages + taxesLicenses + employeeBenefits);

        // Full comp (incl. partner guaranteed payments) kept for breakdown
        // display but NOT used as the payroll comparison base.
        const totalCompensation =
          Number(taxData.total_compensation) ||
          employeeCompensation + guaranteedPayments;

        byYear[year] = {
          grossReceipts: Number(taxData.gross_receipts) || 0,
          cogs: Number(taxData.cost_of_goods_sold || taxData.cogs) || 0,
          wages,
          guaranteedPayments,
          taxesLicenses,
          employeeBenefits,
          employeeCompensation,
          totalCompensation,
        };
      }
    });

    return byYear;
  }, [tax1065Files]);

  // Initialize year basis defaults
  useMemo(() => {
    const years = new Set([
      ...Object.keys(plAnnualTotals).map(Number),
      ...Object.keys(tax1065ByYear).map(Number)
    ]);

    const defaults: Record<number, string> = {};
    years.forEach(year => {
      if (!yearBasis[year]) {
        defaults[year] = 'bank_cash'; // Default to bank-derived
      }
    });

    if (Object.keys(defaults).length > 0) {
      setYearBasis(prev => ({ ...prev, ...defaults }));
    }
  }, [plAnnualTotals, tax1065ByYear]);

  // Build year comparisons
  const yearComparisons = useMemo((): YearComparison[] => {
    const years = new Set([
      ...Object.keys(plAnnualTotals).map(Number),
      ...Object.keys(tax1065ByYear).map(Number)
    ]);

    return Array.from(years)
      .filter(year => plAnnualTotals[year] && tax1065ByYear[year])
      .sort((a, b) => a - b)
      .map(year => {
        const pl = plAnnualTotals[year];
        const tax = tax1065ByYear[year];
        const basis = yearBasis[year] || 'bank_cash';
        const yearExpl = varianceExplanations?.[String(year)];
        const revenueExplainedByMerchant = hasMerchantReconciliation(
          yearExpl?.revenue?.factors
        );

        const revDiff = pl.revenue - tax.grossReceipts;
        const revPct = tax.grossReceipts ? (revDiff / tax.grossReceipts) * 100 : 0;

        // Compare P&L payroll against EMPLOYEE compensation on the 1065
        // (Lines 9 + 14 + 19 — wages + employer taxes + benefits).  Do NOT
        // include Line 10 guaranteed payments to partners here; those are
        // partner draws, not payroll, and appear on an accrual P&L as a
        // separate line.  Fall back to wages-only when expanded fields
        // aren't available (pre-v3.0 parser output).
        const taxPayrollBase =
          tax.employeeCompensation ||
          (tax.wages + tax.taxesLicenses + tax.employeeBenefits) ||
          tax.wages;
        const payrollDiff = pl.payroll - taxPayrollBase;
        const payrollPct = taxPayrollBase ? (payrollDiff / taxPayrollBase) * 100 : 0;

        const cogsDiff = pl.cogs - tax.cogs;
        const cogsPct = tax.cogs ? (cogsDiff / tax.cogs) * 100 : 0;

        // If merchant statements explain the revenue gap, exclude the
        // revenue bucket from the "worst variance" calculation that drives
        // the year-level summary counts.  Matches the header-pill logic.
        const maxVariance = revenueExplainedByMerchant
          ? Math.max(Math.abs(payrollPct), Math.abs(cogsPct))
          : Math.max(Math.abs(revPct), Math.abs(payrollPct), Math.abs(cogsPct));
        const basisThresholds = basisOptions.find(b => b.id === basis)?.thresholds || { review: 5, investigate: 10 };

        let status: 'match' | 'review' | 'investigate' = 'match';
        if (maxVariance > basisThresholds.investigate) status = 'investigate';
        else if (maxVariance > basisThresholds.review) status = 'review';

        return {
          year,
          revenue: { pl: pl.revenue, tax: tax.grossReceipts, diff: revDiff, pct: revPct },
          payroll: { pl: pl.payroll, tax: taxPayrollBase, diff: payrollDiff, pct: payrollPct },
          cogs: { pl: pl.cogs, tax: tax.cogs, diff: cogsDiff, pct: cogsPct },
          status
        };
      });
  }, [plAnnualTotals, tax1065ByYear, yearBasis, varianceExplanations]);

  const toggleRow = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const handleBasisChange = (year: number, basisId: string) => {
    setYearBasis(prev => ({ ...prev, [year]: basisId }));
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    const total = yearComparisons.length;
    const matches = yearComparisons.filter(y => y.status === 'match').length;
    const reviews = yearComparisons.filter(y => y.status === 'review').length;
    const investigations = yearComparisons.filter(y => y.status === 'investigate').length;
    return { total, matches, reviews, investigations };
  }, [yearComparisons]);

  if (tax1065Files.length === 0) {
    return (
      <div
        className="text-center py-12 rounded-xl"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        <Scale className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
        <p className="cs-body" style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>No tax documents to reconcile</p>
        <p className="cs-meta mt-2">Upload 1065 forms to enable P&L cross-check</p>
      </div>
    );
  }

  if (yearComparisons.length === 0) {
    return (
      <div
        className="text-center py-12 rounded-xl"
        style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
      >
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
        <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>No matching years found</p>
        <p className="cs-meta mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
          P&L data and 1065 forms don't have overlapping years for comparison
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with explanation */}
      <div
        className="rounded-xl p-5"
        style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
      >
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
            <Scale className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
          </div>
          <div className="flex-1">
            <h3 className="cs-h3 mb-1">Basis-Aware P&L ↔ 1065 Reconciliation</h3>
            <p className="cs-body leading-relaxed">
              Thresholds adapt based on accounting basis. Tax returns are almost always cash basis.
              Bank-derived P&Ls are inherently cash. Client P&Ls may be either — <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>must be declared per engagement</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Basis Threshold Reference */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        <div
          className="px-5 py-3 flex items-center gap-2"
          style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}
        >
          <Settings2 className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          <span className="cs-label">Basis Pair → Threshold Matrix</span>
        </div>
        <div>
          {basisOptions.map(opt => (
            <div
              key={opt.id}
              className="grid grid-cols-12 gap-4 px-5 py-3 items-center"
              style={{ borderTop: '1px solid var(--color-border-faint)' }}
            >
              <div className="col-span-6">
                <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                  {opt.shortLabel}
                </div>
                <div className="cs-meta mt-0.5">{opt.description}</div>
              </div>
              <div className="col-span-3 text-center">
                <span
                  className="cs-chip inline-flex items-center px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                >
                  Review: ≤{opt.thresholds.review}%
                </span>
              </div>
              <div className="col-span-3 text-center">
                <span
                  className="cs-chip inline-flex items-center px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                >
                  Investigate: &gt;{opt.thresholds.investigate}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div
          className="rounded-lg p-4 text-center"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        >
          <div className="cs-figure-lg">{summaryStats.total}</div>
          <div className="cs-meta mt-1">Years Compared</div>
        </div>
        <div
          className="rounded-lg p-4 text-center"
          style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}
        >
          <div className="cs-figure-lg" style={{ color: 'var(--color-verified)' }}>{summaryStats.matches}</div>
          <div className="cs-meta mt-1" style={{ color: 'var(--color-verified)' }}>Within Tolerance</div>
        </div>
        <div
          className="rounded-lg p-4 text-center"
          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
        >
          <div className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>{summaryStats.reviews}</div>
          <div className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>Need Review</div>
        </div>
        <div
          className="rounded-lg p-4 text-center"
          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
        >
          <div className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>{summaryStats.investigations}</div>
          <div className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>Investigate</div>
        </div>
      </div>

      {/* Year-by-Year Comparison */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        <div
          className="px-6 py-4"
          style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}
        >
          <h3 className="cs-h3 flex items-center gap-2">
            <Calculator className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            Annual Variance Analysis
          </h3>
        </div>

        <div>
          {yearComparisons.map(comp => {
            const yearKey = `year-${comp.year}`;
            const isExpanded = expandedRows.has(yearKey);
            const currentBasis = yearBasis[comp.year] || 'bank_cash';
            const basisInfo = basisOptions.find(b => b.id === currentBasis) || basisOptions[0];
            const yearExplForHeader = varianceExplanations?.[String(comp.year)];
            const revenueExplainedByMerchant = hasMerchantReconciliation(
              yearExplForHeader?.revenue?.factors
            );
            // When merchant statements explain the revenue gap, we exclude
            // revenue from the "worst variance" calculation that drives the
            // year-level status pill.  This prevents an otherwise-clean year
            // from showing "Investigate" just because revenue looks off
            // on paper — the merchant evidence IS the explanation.
            const maxVarianceForHeader = revenueExplainedByMerchant
              ? Math.max(Math.abs(comp.payroll.pct), Math.abs(comp.cogs.pct))
              : Math.max(
                  Math.abs(comp.revenue.pct),
                  Math.abs(comp.payroll.pct),
                  Math.abs(comp.cogs.pct)
                );
            const overallStatus = getVarianceStatus(maxVarianceForHeader, currentBasis);
            const overallStyle = STATUS_STYLES[overallStatus.status];

            return (
              <div key={comp.year} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                {/* Year Header Row */}
                <div
                  className="px-6 py-4 cursor-pointer transition-colors"
                  style={{ background: isExpanded ? 'var(--color-bg-base)' : 'transparent' }}
                  onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => toggleRow(yearKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="cs-h3" style={{ fontWeight: 700 }}>{comp.year}</span>
                      <span
                        className="cs-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
                        style={{ background: overallStyle.bg, borderColor: overallStyle.border, color: overallStyle.color }}
                      >
                        {overallStatus.status === 'match' && <CheckCircle2 className="w-3.5 h-3.5" />}
                        {overallStatus.status === 'review' && <AlertTriangle className="w-3.5 h-3.5" />}
                        {overallStatus.status === 'investigate' && <AlertCircle className="w-3.5 h-3.5" />}
                        {overallStatus.label}
                      </span>
                      {revenueExplainedByMerchant && (
                        <span
                          className="cs-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
                          style={{ background: 'transparent', borderColor: 'var(--color-verified)', color: 'var(--color-verified)' }}
                        >
                          <Store className="w-3.5 h-3.5" />
                          Merchant Evidence
                        </span>
                      )}
                      <span
                        className="cs-chip px-2 py-0.5 rounded"
                        style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                      >
                        {currentBasis === 'bank_cash' ? 'BANK' : currentBasis === 'client_cash' ? 'CASH' : 'ACCRUAL'}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="cs-meta">Revenue</div>
                        <div className="cs-figure" style={{ fontWeight: 600, color: comp.revenue.pct > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
                          {formatPercent(comp.revenue.pct)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="cs-meta">Payroll</div>
                        <div className="cs-figure" style={{ fontWeight: 600, color: comp.payroll.pct > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
                          {formatPercent(comp.payroll.pct)}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                      ) : (
                        <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div
                    className="px-6 py-4 space-y-4"
                    style={{ background: 'var(--color-bg-base)', borderTop: '1px solid var(--color-border-faint)' }}
                  >
                    {/* Basis Selector */}
                    <div
                      className="rounded-lg p-4"
                      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="cs-label">
                            Accounting Basis — {comp.year}
                          </div>
                          <div className="cs-meta mt-0.5" style={{ color: 'var(--color-text-dim)' }}>
                            Select the basis pair to apply correct variance thresholds
                          </div>
                        </div>
                        <span
                          className="cs-chip px-2 py-1 rounded"
                          style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                        >
                          Required Disclosure
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {basisOptions.map(opt => {
                          const isActive = currentBasis === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBasisChange(comp.year, opt.id);
                              }}
                              className="p-3 rounded-lg border text-left transition-all"
                              style={{
                                borderColor: isActive ? 'var(--color-needs-human-tint-bd)' : 'var(--color-border)',
                                background: isActive ? 'var(--color-needs-human-tint)' : 'var(--color-bg-surface)',
                              }}
                            >
                              <div
                                className="cs-body"
                                style={{ fontWeight: 500, color: isActive ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}
                              >
                                {opt.shortLabel}
                              </div>
                              <div className="cs-meta mt-1">
                                Review: {opt.thresholds.review}% · Investigate: {opt.thresholds.investigate}%
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Line Item Cards */}
                    {(() => {
                      const yearExpl = varianceExplanations?.[String(comp.year)];
                      return (
                        <div className="grid grid-cols-3 gap-3">
                          <VarianceCard
                            label="Revenue / Gross Receipts"
                            plValue={comp.revenue.pl}
                            taxValue={comp.revenue.tax}
                            diff={comp.revenue.diff}
                            pct={comp.revenue.pct}
                            basisId={currentBasis}
                            note={basisNotes.revenue[currentBasis]}
                            basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                            factors={yearExpl?.revenue?.factors}
                            onNavigateToRevenueSources={onNavigateToRevenueSources}
                          />
                          <VarianceCard
                            label="Payroll / Wages"
                            plValue={comp.payroll.pl}
                            taxValue={comp.payroll.tax}
                            diff={comp.payroll.diff}
                            pct={comp.payroll.pct}
                            basisId={currentBasis}
                            note={basisNotes.payroll[currentBasis]}
                            basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                            factors={yearExpl?.payroll?.factors}
                          />
                          <VarianceCard
                            label="COGS / Direct Costs"
                            plValue={comp.cogs.pl}
                            taxValue={comp.cogs.tax}
                            diff={comp.cogs.diff}
                            pct={comp.cogs.pct}
                            basisId={currentBasis}
                            note={basisNotes.cogs[currentBasis]}
                            basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                            factors={yearExpl?.cogs?.factors}
                          />
                        </div>
                      );
                    })()}

                    {/* Cross-validation factors */}
                    {(() => {
                      const xvFactors = varianceExplanations?.[String(comp.year)]?.cross_validation?.factors;
                      if (!xvFactors?.length) return null;
                      return (
                        <div
                          className="rounded-lg p-4"
                          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                        >
                          <div className="cs-label mb-2" style={{ color: 'var(--color-needs-human-text)' }}>
                            Cross-Validation
                          </div>
                          <ul className="space-y-1.5">
                            {xvFactors.map((f: VarianceFactor, i: number) => (
                              <li key={i} className="cs-meta flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                                <span>
                                  {f.amount != null && (
                                    <span className="cs-figure" style={{ fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                                      <FinancialValue value={f.amount} />
                                    </span>
                                  )}{' '}
                                  {f.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Decision Rules Reference */}
      <div
        className="rounded-xl p-5"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        <h4 className="cs-h3 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          Core Principle
        </h4>
        <p className="cs-body leading-relaxed">
          Tax returns are almost always cash basis. Bank-derived P&Ls are inherently cash basis.
          When both are cash, variances should be small and explainable. Client-provided P&Ls from
          QuickBooks may be either basis — <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>must be declared and disclosed per engagement</span>.
        </p>
      </div>
    </div>
  );
};

// Sub-component for variance cards
const VarianceCard: React.FC<{
  label: string;
  plValue: number;
  taxValue: number;
  diff: number;
  pct: number;
  basisId: string;
  note: string;
  basisLabel: string;
  factors?: VarianceFactor[];
  onNavigateToRevenueSources?: () => void;
}> = ({ label, plValue, taxValue, diff, pct, basisId, note, basisLabel, factors, onNavigateToRevenueSources }) => {
  const rawStatus = getVarianceStatus(pct, basisId);
  const basis = basisOptions.find(b => b.id === basisId) || basisOptions[0];

  // When the backend emits a merchant_statement_reconciliation factor, the
  // variance is considered explained by third-party evidence regardless of
  // raw percentage.  We swap the pill (and card framing) from an
  // "Investigate" to an "Explained — Merchant Evidence" to reflect
  // that the gap now has a documented explanation.  The factor's presence
  // is the signal; we do NOT recompute thresholds in the UI.
  const merchantFactor = factors?.find(f => f.type === MERCHANT_RECON_FACTOR_TYPE);
  const isMerchantExplained = !!merchantFactor;

  const status = isMerchantExplained
    ? {
        status: 'match' as const,
        label: 'Explained — Merchant Evidence',
      }
    : rawStatus;
  const statusStyle = STATUS_STYLES[status.status];

  // When merchant reconciliation explains the gap, the raw variance number
  // is misleading — the $-270K is closed by $271K of merchant gross.  We
  // show the ADJUSTED variance (residual after merchant evidence) as the
  // primary number, with the raw pre-reconciliation number as muted
  // context.  The residual IS the remaining unexplained variance.
  //
  // `merchantFactor.amount` carries the merchant_statement_gross_sales
  // for the year (positive).  `diff` is `plValue - taxValue` — negative
  // when bank is short of 1065.  Adjusted = diff + merchant_gross.
  const merchantAmount = (merchantFactor?.amount ?? 0);
  const adjustedDiff = isMerchantExplained ? diff + merchantAmount : diff;
  const adjustedPct = isMerchantExplained && taxValue !== 0
    ? (adjustedDiff / taxValue) * 100
    : pct;

  return (
    <div className="rounded-lg border p-4" style={{ background: statusStyle.bg, borderColor: statusStyle.border }}>
      <div className="flex items-center justify-between mb-3">
        <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{label}</span>
        <span
          className="cs-chip inline-flex items-center gap-1 px-2 py-0.5 rounded"
          style={{ color: isMerchantExplained ? 'var(--color-verified)' : statusStyle.color }}
        >
          {isMerchantExplained ? (
            <Store className="w-3 h-3" />
          ) : (
            <>
              {status.status === 'match' && <CheckCircle2 className="w-3 h-3" />}
              {status.status === 'review' && <AlertTriangle className="w-3 h-3" />}
              {status.status === 'investigate' && <AlertCircle className="w-3 h-3" />}
            </>
          )}
          {status.label}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="cs-meta">P&L ({basisLabel})</span>
          <span className="cs-figure" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}><FinancialValue value={plValue} /></span>
        </div>
        <div className="flex justify-between items-center">
          <span className="cs-meta">1065 (Cash)</span>
          <span className="cs-figure" style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}><FinancialValue value={taxValue} /></span>
        </div>
        <div className="pt-2" style={{ borderTop: '1px solid var(--color-border)' }}>
          {isMerchantExplained ? (
            <>
              {/* Raw pre-reconciliation variance — muted, struck through.
                  Preserved for transparency: the defendant will cite this
                  raw number, so the forensic accountant needs to see it
                  acknowledged before the adjusted figure. */}
              <div className="flex justify-between items-center">
                <span className="cs-meta" style={{ fontSize: '11px', color: 'var(--color-text-dim)' }}>Raw variance (pre-merchant)</span>
                <span className="cs-figure line-through" style={{ color: 'var(--color-text-dim)', fontSize: '0.75rem' }}>
                  {diff > 0 ? '+' : ''}<FinancialValue value={diff} /> ({formatPercent(pct)})
                </span>
              </div>
              {/* Adjusted variance — residual after merchant evidence.
                  This is the real remaining unexplained gap and should be
                  near zero when the merchant statements fully reconcile. */}
              <div className="flex justify-between items-center mt-1">
                <span className="cs-meta" style={{ fontWeight: 600, color: 'var(--color-verified)' }}>Adjusted (+ merchant evidence)</span>
                <span className="cs-figure" style={{ fontWeight: 700, color: 'var(--color-verified)' }}>
                  {adjustedDiff > 0 ? '+' : ''}<FinancialValue value={adjustedDiff} /> ({formatPercent(adjustedPct)})
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center">
              <span className="cs-meta">Variance</span>
              <span className="cs-figure" style={{ fontWeight: 700, color: pct > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
                {diff > 0 ? '+' : ''}<FinancialValue value={diff} /> ({formatPercent(pct)})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Variance bar with threshold markers.  When merchant-explained, the
          bar reflects the ADJUSTED residual percentage, not the raw gap —
          a near-zero green sliver instead of a full-width orange bar. */}
      <div className="mt-3 h-1.5 rounded-full overflow-hidden relative" style={{ background: 'var(--color-bg-elevated)' }}>
        {/* Threshold markers */}
        <div
          className="absolute h-3 w-0.5 -top-0.5"
          style={{ left: `${Math.min(basis.thresholds.review * 3, 100)}%`, background: 'var(--color-needs-human-tint-bd)' }}
        />
        <div
          className="absolute h-3 w-0.5 -top-0.5"
          style={{ left: `${Math.min(basis.thresholds.investigate * 3, 100)}%`, background: 'var(--color-brand)' }}
        />
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(Math.abs(isMerchantExplained ? adjustedPct : pct) * 3, 100)}%`,
            background: isMerchantExplained ? 'var(--color-verified)' :
              status.status === 'match' ? 'var(--color-verified)' :
              'var(--color-brand)',
          }}
        />
      </div>

      {/* Dynamic claim-specific factors take precedence over the generic
          basis note.  The basis note (one-size-fits-all guidance like "Both
          cash basis — variance should be small. Large gap suggests
          non-revenue deposits...") contradicts the per-claim automated
          investigation below it, so we suppress it whenever there's at
          least one factor to show.  For clean/within-tolerance cards with
          no factors, the basis note stays as sensible default guidance. */}
      {factors && factors.length > 0 ? (
        <div className="mt-3 pt-2.5" style={{ borderTop: '1px solid var(--color-border)' }}>
          {/* Headline: merchant reconciliation gets a prominent badge
              with store icon, placed above any other factors.  The factor's
              text is rendered verbatim — the backend is responsible for
              phrasing (platforms reconciled, % explained, etc). */}
          {merchantFactor && (
            <div className="mb-2.5 rounded-md p-2.5" style={{ border: '1px solid var(--color-verified)', background: 'transparent' }}>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded" style={{ background: 'transparent' }}>
                  <Store className="w-3.5 h-3.5" style={{ color: 'var(--color-verified)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="cs-eyebrow" style={{ fontSize: '10px', fontWeight: 700, color: 'var(--color-verified)' }}>
                      Merchant Statement Reconciliation
                    </span>
                    {typeof merchantFactor.count === 'number' && merchantFactor.count > 0 && (
                      <span className="cs-chip px-1.5 py-0.5 rounded" style={{ fontSize: '10px', color: 'var(--color-verified)', background: 'transparent' }}>
                        {merchantFactor.count} {merchantFactor.count === 1 ? 'platform' : 'platforms'}
                      </span>
                    )}
                  </div>
                  <p className="cs-meta leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                    {merchantFactor.text}
                  </p>
                  {onNavigateToRevenueSources && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToRevenueSources();
                      }}
                      className="mt-1.5 inline-flex items-center gap-1 hover:underline"
                      style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-verified)' }}
                    >
                      View reconciliation
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Remaining factors render in the standard bulleted list. */}
          {factors.some(f => f.type !== MERCHANT_RECON_FACTOR_TYPE) && (
            <>
              <div className="cs-eyebrow flex items-center gap-1.5 mb-1.5" style={{ fontSize: '11px', color: 'var(--color-needs-human-text)' }}>
                <AlertCircle className="w-3 h-3" />
                Automated Investigation
              </div>
              <ul className="space-y-1.5">
                {factors
                  .filter(f => f.type !== MERCHANT_RECON_FACTOR_TYPE)
                  .map((f, i) => (
                    <li key={i} className="cs-meta flex items-start gap-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }}>&#8226;</span>
                      <span>{f.text}</span>
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        /* Basis context note — only shown when no claim-specific factors exist */
        <p className="mt-3 cs-meta italic leading-relaxed">{note}</p>
      )}
    </div>
  );
};

export default TaxReconciliation;
